/* eslint-disable no-secrets/no-secrets */
import {
    Component, OnInit, Input, OnChanges, SimpleChanges, Output, EventEmitter, OnDestroy,
} from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, UntypedFormBuilder, Validators } from '@angular/forms';
import { concatLatestFrom } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject, forkJoin, of } from 'rxjs';
import { filter, first, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { RgpdModalComponent } from '../../modals/rgpd/rgpd.component';
import { TermsConditions } from '../../models/terms-conditions.model';
import { OpenDocument, SetCheckAllTermsAndConditions } from '../../store/terms-conditions.actions';

import { InputTermsConditionsService } from './input-terms-conditions.service';

import { Address } from '@api/address/models/address.models';
import { Cart } from '@api/cart/cart.models';
import { PaymentPayload } from '@api/payment/payment.model';
import { MoneyHelpers } from '@api/util/money.helpers';
import { MarketConfigurable } from '@core/domain/impl/market-configurable.impl';
import { CurrencyInfo } from '@core/domain/models/store.models';
import { Resolutions } from '@core/domain/state/core.state';
import { getCmsConfigValue, getCountryCode, getCurrencyInfo, getLanguageisRTL, getResolution, getStoreAndLanguage, getXConfigValue } from '@core/infrastructure/state/core.selectors';
import { MspotService } from '@features/mspot';
import { PublicUser } from '@features/user';
import { getSelectedAddress, getBillingAddress } from '@shared/address/store/address/address.selectors';
import { OpenPrivacyPolicyAnalytics } from '@shared/analytics/store/app-events-analytics.actions';
import { getSelectedPaymentMethod, getPaymentDetails } from '@shared/payment/store';
import { RgpdService } from '@shared/services/rgpd.service';
import { getShopCart } from '@shared/shopcart/store/shopcart.selectors';
import { AuthenticationConditionsModalComponent } from '@shared/terms-conditions/modals/authentication-conditions/authentication-conditions.component';
import { isCheckAllTermsAndConditions } from '@shared/terms-conditions/store/terms-conditions.selectors';
import { getUserSelector } from '@shared/user/store/user.selectors';
import { DialogMargins, DialogOptions, DialogService, DialogSizes } from '@ui/dialog/dialog.service';

const USER_GUEST_TYPE = 'G';

// eslint-disable-next-line import/exports-last
export interface InputTermsSections {
    type: string;
    required: boolean;
}

type TermsConditionsSetup = Record<string, boolean | (() => boolean)>;

@Component({
    selector: 'input-terms-conditions',
    templateUrl: './input-terms-conditions.component.html',
    styleUrls: ['./input-terms-conditions.component.scss'],
    providers: [DialogService],
})
// eslint-disable-next-line max-len
export class InputTermsConditionsComponent extends MarketConfigurable<TermsConditionsSetup> implements OnInit, OnChanges, OnDestroy {
    protected readonly config: Record<string, TermsConditionsSetup> = {
        TR: {
            showTermsAll: () => false,
            getMspotTemplateData: true,
        },
        KR: {
            showTermsAll: () => !!this.sections?.find(x => x.type === 'personalInformationConditions'),
            getMspotTemplateData: false,
        },
        default: {
            showTermsAll: () => false,
            getMspotTemplateData: false,
        },
    };

    private readonly onDestroy$ = new Subject<void>();

    @Input() public countryCode: string;

    @Input() public form: UntypedFormGroup;

    @Input() public sections: InputTermsSections[];

    @Input() public implicitRegisterConditions: boolean;

    @Input() public isModal: boolean;

    @Output() public readonly formReady: EventEmitter<UntypedFormGroup> = new EventEmitter<UntypedFormGroup>();

    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() public readonly onTermsAccept: EventEmitter<boolean> = new EventEmitter<boolean>();

    public optIn: boolean;

    public rgpdEnabled: boolean;

    public isMobile: boolean;

    public isTurkey: boolean;

    public isKorea: boolean;

    public isChina: boolean;

    public isKoreanLang: boolean;

    public guestUser: boolean;

    public isOpening: boolean = false;

    public arrLineTerms$: Observable<TermsConditions[]> = combineLatest([
        this.store.select(getUserSelector),
        this.rgpdService.isRgpdEnabled().pipe(
            tap((isRGPDEnabled) => {
                this.rgpdEnabled = isRGPDEnabled;
            })
        ),
        this.service.translations$,
    ]).pipe(
        first(),
        switchMap(([currentUser, isRGPDEnabled]) => {
            // Si RGPD está activo
            if (isRGPDEnabled) {
                // Generamos el contenido de los términos y condiciones específico para RGPD
                return this.generateLineTermsRGPD(currentUser);
            }
            // Generamos el contenido de los términos y condiciones sin RGPD
            return this.generateLineTerms();
        })
    );

    public newsletterModalModeActive$: Observable<boolean> = this.store.select(getCmsConfigValue('NL-popup-politicasPrivacidad'));

    public allTermsAndConditionsChecked$: Observable<boolean> = this.store.select(isCheckAllTermsAndConditions);

    public termsData: any;

    public lineTerms: TermsConditions;

    public lineTermsNewsletter: TermsConditions;

    public purchaseConditions: boolean;

    public newsletterConditions: boolean;

    public dataTraspasOutConditions: boolean;

    public newsletterConditionsAccount: boolean;

    public giftCardConditions: boolean;

    public registerConditionsUnder14: boolean;

    public registerConditions: boolean;

    public socialConditions: boolean;

    public addressConditions: boolean;

    public hidePrivacyPolicy: boolean;

    public productStatusConditions: boolean;

    public storageOfPaymentConditions: boolean;

    public giftCardConditionsReturn: boolean;

    public contactConditions: boolean;

    public feelConditions: boolean;

    public feelRegisterConditions: boolean;

    public personalInformationConditions: boolean;

    public personalInformationOverseasConditions: boolean;

    public personalInformationNewsletterConditions: boolean;

    public dataTransferConditions: boolean;

    public sensitiveDataConditions: boolean;

    public redeemFeelGiftConditions: boolean;

    public showTermsAll: boolean;

    public termsAll: boolean;

    public idRandom: string;

    public openOnSelected;

    public isRtl$: Observable<boolean> = this.store.select(getLanguageisRTL);

    private readonly countryAndLanguageSetup$ = this.store.select(getStoreAndLanguage).pipe(
        first(),
        tap(({ countryCode, languageId }) => {
            this.isTurkey = countryCode === 'TR' || countryCode === 'TK';
            this.isKorea = countryCode === 'KR';
            this.isChina = countryCode === 'CN';
            this.isKoreanLang = parseInt(languageId) === -9;
        })
    );

    public mspotTemplateData: Observable<unknown> = this.store.select(getCountryCode).pipe(
        map((countryCode) => this.getConfigByMarketCode(countryCode).getMspotTemplateData),
        filter((isGetMspotTemplateData) => isGetMspotTemplateData),
        switchMap(() => {
            return combineLatest([
                this.store.select(getSelectedAddress),
                this.store.select(getBillingAddress),
                this.store.select(getShopCart),
                this.store.select(getSelectedPaymentMethod),
                this.store.select(getPaymentDetails),
                this.store.select(getCurrencyInfo),
            ]).pipe(
                map(([selectedAddress, billingAddress, shopCart, selectedPaymentMethod, paymentDetails, currencyInfo]) => {
                    return this.getDSCDataObject(
                        selectedAddress,
                        billingAddress,
                        shopCart,
                        selectedPaymentMethod,
                        paymentDetails,
                        currencyInfo
                    );
                })
            );
        })
    );

    constructor(
        private readonly formBuilder: UntypedFormBuilder,
        private readonly modalService: DialogService,
        private readonly rgpdService: RgpdService,
        private readonly service: InputTermsConditionsService,
        private readonly store: Store,
        private readonly mspotService: MspotService,
    ) {
        super();

        this.idRandom = `${Date.now()}`;
    }

    public ngOnInit(): void {
        this.handleResolutionChanges();

        this.showTermsAll = this.getConfigByMarketCode(this.countryCode).showTermsAll();

        this.store.select(getXConfigValue('NEWSLETTER_DOUBLE_OPT_IN'))
            .pipe(first())
            .subscribe(
                (isNewsletterOptIn) => { this.optIn = !!isNewsletterOptIn; }
            );
    }

    public ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.countryCode?.isFirstChange()) {
            this.setSections();
            this.prepareUntypedFormGroup();
        }
    }

    public trackByIndex(index: number): string {
        return `termsAndConditions_${index}`;
    }

    private handleResolutionChanges(): void {
        this.store.select(getResolution).pipe(
            takeUntil(this.onDestroy$)
        ).subscribe((resolution) => {
            this.isMobile = resolution === Resolutions.MOBILE;
        });
    }

    public termsChange(event): void {
        this.onTermsAccept.emit(event.currentTarget.checked);
    }

    private setSections(): void {
        this.purchaseConditions = this.hasSection('purchaseConditions');
        this.newsletterConditions = this.hasSection('newsletterConditions');
        this.newsletterConditionsAccount = this.hasSection('newsletterConditionsAccount');
        this.giftCardConditions = this.hasSection('giftCardConditions');
        this.registerConditions = this.hasSection('registerConditions');
        this.socialConditions = this.hasSection('socialConditions');
        this.addressConditions = this.hasSection('addressConditions');
        this.hidePrivacyPolicy = this.hasSection('hidePrivacyPolicy');
        this.productStatusConditions = this.hasSection('productStatusConditions');
        this.storageOfPaymentConditions = this.hasSection('storageOfPaymentConditions');
        this.giftCardConditionsReturn = this.hasSection('giftCardConditionsReturn');
        this.contactConditions = this.hasSection('contactConditions');
        this.feelConditions = this.hasSection('feelConditions');
        this.feelRegisterConditions = this.hasSection('feelRegisterConditions');
        this.redeemFeelGiftConditions = this.hasSection('redeemFeelGiftConditions');
        this.personalInformationConditions = this.hasSection('personalInformationConditions');
        this.personalInformationOverseasConditions = this.hasSection('personalInformationOverseasConditions');
        this.personalInformationNewsletterConditions = this.hasSection('personalInformationNewsletterConditions');
        this.dataTraspasOutConditions = this.hasSection('dataTraspasOut');
        this.dataTransferConditions = this.hasSection('dataTransferConditions');
        this.sensitiveDataConditions = this.hasSection('sensitiveDataConditions');
        this.registerConditionsUnder14 = this.hasSection('registerConditionsUnder14');
    }

    private prepareUntypedFormGroup(): void {
        this.form = this.formBuilder.group({});
        this.formReady.emit(this.form);
    }

    private hasSection(sectionName: string): boolean {
        return this.sections?.some(item => item.type === sectionName);
    }

    public checkAllTerms(event) {
        this.store.dispatch(SetCheckAllTermsAndConditions({ checkAllTermsAndConditions: event.currentTarget.checked }));
        const value = event.currentTarget.checked;
        Object.keys(this.form.controls).forEach(key => {
            this.form.controls[key].setValue(value);
        });
    }

    private getTermsAndConditions(termsConditions: string[]) {
        return this.countryAndLanguageSetup$.pipe(
            switchMap((countryAndLanguage) => {
                const _termsConditions = termsConditions.map((termCondition) => {
                    return this.service.getTermCondition(termCondition, countryAndLanguage);
                });
                return forkJoin(_termsConditions);
            }),
            map((_termsConditions) => {
                const __termsConditions: TermsConditions[] = [];
                _termsConditions.forEach((innerTerms) => {
                    __termsConditions.push(...innerTerms);
                });
                return __termsConditions;
            }),
            tap((_termsConditions) => {
                _termsConditions.forEach((term) => {
                    const validators = this.sections.find(
                        item => term.type.includes(item.type)
                    )?.required ? Validators.requiredTrue : [];

                    this.form.addControl(`termsAndConditionsCheck_${term.label}`, new UntypedFormControl('', validators));
                });
            }),
            tap((_termsConditions) => {
                if (!this.isMobile && _termsConditions[0].tooltipMode) {
                    this.addTooltipData(_termsConditions);
                }
            })
        );
    }

    private generateLineTermsRGPD(currentUser: PublicUser): Observable<TermsConditions[]> {
        const termsConditions: string[] = [];

        if (this.contactConditions) {
            termsConditions.push('getContactConditionsRGPD');
        }

        // Casuística para los términos de alta en Feel
        if (this.feelConditions) {
            termsConditions.push('getFeelConditionsRGPD');
        }
        if (this.feelRegisterConditions) {
            termsConditions.push('getFeelRegisterConditionsRGPD');
        }

        if (this.newsletterConditions) {
            if (this.registerConditions || this.feelRegisterConditions) {
                termsConditions.push('getNewsletterConditionsRegisterCommonRGPD');
            } else {
                termsConditions.push('getNewsletterConditionsRGPD');
            }
        }

        if (this.productStatusConditions) {
            termsConditions.push('getProductStatusConditionsRGPD');
        }

        if (this.guestUser && this.addressConditions) {
            termsConditions.push('getAddressConditionsRGPD');
        }

        if (this.purchaseConditions || this.addressConditions) {
            if (currentUser.userType === USER_GUEST_TYPE) {
                termsConditions.push('getPurchaseConditionsRGPDGuest');
            } else {
                termsConditions.push('getPurchaseConditionsRGPD');
            }
        }

        if (this.giftCardConditions) {
            termsConditions.push('getGiftCardConditionsRGPD');
        }

        if (this.giftCardConditionsReturn) {
            termsConditions.push('getGiftCardConditionsReturnRGPD');
        }

        if (this.storageOfPaymentConditions) {
            termsConditions.push('getStorageOfPaymentConditionsRGPD');
        }

        if (this.socialConditions) {
            termsConditions.push('getSocialConditionsRGPD');
        }

        if (this.dataTraspasOutConditions) {
            termsConditions.push('getDataTraspasOut');
        }

        if (this.registerConditions && !this.implicitRegisterConditions) {
            termsConditions.push('getRegisterConditionsRGPD');
        }

        return this.getTermsAndConditions(termsConditions);
    }

    private generateLineTerms(): Observable<TermsConditions[]> {
        const termsConditions: string[] = [];

        if (this.registerConditionsUnder14) {
            termsConditions.push('getRegisterConditionsUnder14');
        }

        if (this.feelRegisterConditions || this.feelConditions) {
            termsConditions.push('getFeelRegisterConditions');
        }

        if (this.productStatusConditions) {
            if (!this.optIn) {
                termsConditions.push('getProductStatusConditionsOptIn');
            } else {
                termsConditions.push('getProductStatusConditions');
            }
        }

        // Casuística para los términos de registro, direcciones, actividad social y contacto
        if (this.addressConditions || this.socialConditions) {
            if (!this.isKorea && !this.hidePrivacyPolicy) {
                if (this.optIn) {
                    termsConditions.push('getRegisterAddressSocialContactConditionsOptIn');
                } else {
                    termsConditions.push('getRegisterAddressSocialContactConditions');
                }
            }
        }

        if (this.registerConditions && !this.implicitRegisterConditions && !this.isKorea && !this.hidePrivacyPolicy) {
            if (!this.isKorea && !this.hidePrivacyPolicy) {
                if (!this.optIn) {
                    termsConditions.push('getRegisterAddressSocialContactConditionsOptIn');
                } else {
                    termsConditions.push('getRegisterAddressSocialContactConditions');
                }
            }
        }

        if (this.contactConditions) {
            if (!this.isKorea && !this.hidePrivacyPolicy) {
                if (!this.optIn) {
                    termsConditions.push('getContactConditionsOptIn');
                } else {
                    termsConditions.push('getContactConditions');
                }
            }
        }

        if (this.personalInformationConditions) {
            termsConditions.push('getPersonalInformationConditions');
        }

        if (this.personalInformationOverseasConditions) {
            termsConditions.push('getPersonalInformationOverseasConditions');
        }

        if (this.personalInformationNewsletterConditions) {
            termsConditions.push('getPersonalInformationNewsletterConditions');
        }

        if (this.storageOfPaymentConditions) {
            termsConditions.push('getStorageOfPaymentConditions');
        }

        if (this.giftCardConditions || this.giftCardConditionsReturn) {
            termsConditions.push('getGiftCardConditions');
        }

        if (this.purchaseConditions) {
            termsConditions.push('getPurchaseConditions');
        }

        if (this.newsletterConditions || this.newsletterConditionsAccount) {
            if (this.isKorea) {
                termsConditions.push('getNewsletterConditionsKorea');
            } else if (this.registerConditions) {
                termsConditions.push('getNewsletterConditionsRegisterCommon');
            } else {
                termsConditions.push('getNewsletterConditions');
            }
        }

        if (this.redeemFeelGiftConditions) {
            termsConditions.push('getRedeemFeelGiftConditions');
        }

        if (this.dataTransferConditions) {
            termsConditions.push('getDataTransferConditions');
        }

        if (this.sensitiveDataConditions) {
            termsConditions.push('getSensitiveDataConditions');
        }

        return this.getTermsAndConditions(termsConditions);
    }

    private getDSCDataObject(
        selectedAddress: Address,
        billingAddress: Address,
        shopCart: Cart,
        selectedPaymentMethod,
        paymentDetails: PaymentPayload,
        currencyInfo: CurrencyInfo
    ) {
        const cardNumber = paymentDetails.number ? '*'.repeat(paymentDetails.number?.length - 4) + paymentDetails.number?.slice(paymentDetails.number?.length - 4, paymentDetails.number?.length) : paymentDetails.printablePan;

        return {
            termsConditionsCtrl: {
                address: billingAddress,
                shippingAddress: selectedAddress,
                ItxPaymentSvc: {
                    selected: selectedPaymentMethod,
                    getPaymentTitle: () => paymentDetails.holder,
                    getCardAlias: () => `${selectedPaymentMethod.name} ${cardNumber}`,
                },
                shopCart: {
                    totalOrder: shopCart.totalOrder,
                    items: shopCart.items,
                },
                formatPrice: (price: number) =>
                    MoneyHelpers.formatPriceWithCurrencySymbol(price, currencyInfo),
            },
            items: shopCart.items,
        };
    }

    public openPrivacyPolicy(layer: string): void {
        this.arrLineTerms$.pipe(
            first(),
            concatLatestFrom(() => this.newsletterModalModeActive$),
            switchMap(([arrLineTerms, newsletterModalModeActive]) => {
                const newsletterModalMode = this.newsletterConditions && newsletterModalModeActive;
                const isModal = this.rgpdEnabled || newsletterModalMode || this.isModal;
                if (!isModal || layer === 'NO_LAYER') {
                    this.store.dispatch(OpenDocument({ name: 'privacyPolicy' }));
                    return of([]);
                }
                const isTooltip = this.isMobile || arrLineTerms[0].tooltipMode;
                if (this.isMobile || !isTooltip) {
                    if (!layer) {
                        // eslint-disable-next-line no-param-reassign
                        layer = 'layer_1';
                    }
                    return this.rgpdService.getRGPDContent(layer).pipe(first());
                }
            })
        ).subscribe((content: any) => {
            if (content && Object.keys(content).length > 0) {
                this.openRgpdModal(content);
            }
        });
        this.store.dispatch(OpenPrivacyPolicyAnalytics());
    }

    private openRgpdModal(content: any): void {
        this.modalService.openModalFromComponent(
            'dev.rgpdModal.title',
            RgpdModalComponent,
            content,
            DialogSizes.SIZE_S,
            DialogMargins.MARGIN_40,
            [DialogOptions.FULL_MOBILE]
        )
            .afterClosed$
            .pipe(first())
            .subscribe(() => {
                this.modalOpened();
            });
    }

    // Estos métodos se llaman dinamicamente y se asignan desde el servicio, no se pueden eliminar si no se tiene en cuenta el servicio

    public windowOpenFeelTermsConditions(): void {
        if (this.isChina) {
            this.store.dispatch(OpenDocument({ name: 'feelTermsConditionsChina' }));
        } else {
            this.store.dispatch(OpenDocument({ name: 'feelTermsConditions' }));
        }
        this.modalOpened();
    }

    public windowOpenFeelPrivacyPolicy(): void {
        if (this.isChina) {
            this.store.dispatch(OpenDocument({ name: 'feelPrivacyPolicyChina' }));
        } else {
            this.store.dispatch(OpenDocument({ name: 'feelPrivacyPolicy' }));
        }
        this.modalOpened();
    }

    public openPurchaseCondition(): void {
        this.store.dispatch(OpenDocument({ name: 'generalConditions' }));
        this.modalOpened();
    }

    public openKoreaPurchaseCondition(): void {
        this.store.dispatch(OpenDocument({ name: 'privacyPolicyPaymentKr' }));
        this.modalOpened();
    }

    public windowOpenPrivacyPolicy(): void {
        if (this.isModal) {
            this.openPrivacyPolicy('layer_1');
        } else {
            this.store.dispatch(OpenDocument({ name: 'privacyPolicy' }));
            this.modalOpened();
        }
    }

    public openGiftCardConditions(): void {
        this.store.dispatch(OpenDocument({ name: 'giftCardConditions' }));
        this.modalOpened();
    }

    public openCancellationPolicies(): void {
        this.store.dispatch(OpenDocument({ name: 'cancelationPolicies' }));
        this.modalOpened();
    }

    public openKoreaPersonalInformation(mspotName: string): void {
        this.mspotService.getMspot(mspotName).pipe(take(1)).subscribe((mspot) => {
            this.modalService.openModalFromComponent(
                null,
                AuthenticationConditionsModalComponent,
                {
                    value: mspot.value,
                },
                DialogSizes.SIZE_S,
                DialogMargins.MARGIN_40,
                [DialogOptions.FULL_MOBILE]
            )
                .afterClosed$
                .pipe(first())
                .subscribe(() => {
                    this.modalOpened();
                });
        });
    }

    private addTooltipData(termsConditions: TermsConditions[]): void {
        this.rgpdService.getRGPDContent(termsConditions[0].layer).subscribe((data: any) => {
            this.termsData = data;
        });
    }

    public open($event: Event, lineTerms: TermsConditions, message: any) {
        $event.preventDefault();
        if (this.isOpening) return;
        this.isOpening = true;
        this[message?.clickText](lineTerms.layer);
    }

    private modalOpened() {
        setTimeout(() => { this.isOpening = false; }, 700);
    }
}
